import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import { useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '../../../../../../CommonComponents/OPL/Menu/Menu';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { ICON_DEFAULT_VALUE, TaskIcons, TaskNames } from '../../Tasks.constants';
import { ListItem } from '../../../../../../CommonComponents/OPL/List/ListItem';
import { InputMask } from '../../../../../../CommonComponents/OPL/Input/InputMask';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { PlanActionStepDTO } from '../../../../../types/planActionStepsDTO';
import { AssignTo, useAssignList } from './useAssignList';
import { AssigneeTypes } from '../../Tasks.types';
import { useUpsertActionStepMutation } from '../../api/useUpsertActionStepMutation';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { useFpId } from '../../../../../hooks/useFpId';
import { useFinancialPlanQuery } from '../../../../../api/useFinancialPlanQuery';
import { queryKeys } from '../../../../../api/queryKeys';
import { queryKeys as actionStepsReportQueryKeys } from '../../../../../../ActionStepsReport/api/queryKeys';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { addEditActionStepValidation } from './AddEditActionStep.validation';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import {
  isAdviserArea as selectIsAdviserArea,
  selectCurrentClient,
} from '../../../../../../CommonSelectors/CommonAreaSelector';
import { PulseCheckCategoryIcon } from '../PulseCheckCategoryIcon/PulseCheckCategoryIcon';
import { PulseCheckCategory } from '../../../../../types/goalsDTO';
import { useActionStepsQuery } from '../../../../../../ActionStepsReport/api/useActionStepsQuery';

interface FormFields {
  text: string;
  dueDate?: string;
  icon?: number;
  assignTo: AssignTo | null;
  assetBucketIndex: number | null;
}

interface GetAssignTo {
  assigneeId?: number | null;
  assigneeType?: number | null;
  data: AssignTo[];
}

const getAssignTo = ({ assigneeId, assigneeType, data }: GetAssignTo): AssignTo | null => {
  if (assigneeId) {
    return data?.find(({ value }) => value === assigneeId) || null;
  }
  if (assigneeType === AssigneeTypes.Primary) {
    return data?.find(({ type }) => type === AssigneeTypes.Primary) || null;
  }
  if (assigneeType === AssigneeTypes.Partner) {
    return data?.find(({ type }) => type === AssigneeTypes.Partner) || null;
  }
  return null;
};

interface AddEditActionStepProps extends Partial<PlanActionStepDTO> {
  setFormVisibility: (showHide: boolean) => void;
  setIsCurrentActionStepLoading?: (isLoading: boolean) => void;
  debtType?: number;
  pulseCheckCategory?: PulseCheckCategory;
  isReport?: boolean;
  goalId?: string;
  showAssetBucketing?: boolean;
  availableAssetBuckets?: { Title: string }[];
}

export const AddEditActionStep = (props: AddEditActionStepProps) => {
  const isAdvisor = useSelector(selectIsAdviserArea);

  const {
    Icon: icon,
    setFormVisibility,
    Text: text,
    DueDate: dueDate,
    AssigneeId: assigneeId,
    AssigneeType: assigneeType,
    AssetBucketIndex: assetBucketIndex,
    setIsCurrentActionStepLoading = () => {},
    Id: id,
    debtType,
    IsGenerated: isGenerated,
    pulseCheckCategory,
    isReport,
    goalId,
    showAssetBucketing,
    availableAssetBuckets,
  } = props;

  const queryClient = useQueryClient();

  const { Id: clientId } = useSelector(selectCurrentClient);
  const { goalId: routeGoalId } = useRouteParams();
  const fpId = useFpId();
  const { data, isLoading: isLoadingAssignList } = useAssignList();

  const { isFetching } = isReport ? useActionStepsQuery() : useFinancialPlanQuery();

  const {
    mutateAsync: createUpdateTask,
    isLoading: isLoadingUpdateTask,
    error,
    isError,
  } = useUpsertActionStepMutation();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    getValues,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      text: text || '',
      dueDate: dueDate ? moment(dueDate).format('MM/DD/YYYY') : '',
      icon: icon || ICON_DEFAULT_VALUE,
      assignTo: null,
      assetBucketIndex,
    },
    resolver: yupResolver(addEditActionStepValidation),
  });

  useEffect(() => {
    const { assignTo } = getValues();

    if (data && !assignTo) {
      setValue(
        'assignTo',
        getAssignTo({
          assigneeId,
          assigneeType,
          data,
        }),
      );
    }
  }, [data, setValue, getValues, assigneeId, assigneeType]);

  if (!isAdvisor) {
    return null;
  }

  const onSubmit = async formData => {
    const submitData = {
      ...props,
      Text: formData.text,
      Icon: formData.icon,
      DueDate: formData.dueDate,

      AssigneeId:
        formData.assignTo && formData.assignTo.value >= isAdvisor ? formData.assignTo.value : null,

      AssigneeType: formData.assignTo ? formData.assignTo.type : null,

      ...(formData.assignTo &&
      formData.assignTo.type === (AssigneeTypes.Primary || AssigneeTypes.Partner)
        ? { AssignTo: formData.assignTo }
        : {}),
      clientId,
      fpId,
      GoalId: isReport ? goalId : routeGoalId,

      ...(debtType !== undefined ? { DebtType: debtType } : {}),

      Category: pulseCheckCategory,
      AssetBucketIndex: formData.assetBucketIndex,
    };

    try {
      await createUpdateTask(submitData);

      // On Edit, close the form, show the loading indicator on the field
      if (id) {
        setFormVisibility(false);
      }

      const queryKey = isReport
        ? actionStepsReportQueryKeys.actionSteps(clientId, fpId)
        : queryKeys.financialPlan(clientId, fpId);

      setIsCurrentActionStepLoading(true);
      await queryClient.invalidateQueries(queryKey);
      setIsCurrentActionStepLoading(false);

      // On Add, only close the form after all requests are done, loading indicator is in the form button
      if (!id) {
        setFormVisibility(false);
      }
    } catch {
      //  ignore
    }
  };

  const assetBucketOptions = (availableAssetBuckets || []).map(({ Title }, index) => ({
    value: index,
    label: Title,
  }));

  return (
    <>
      {isError && <Alert my="8" variant="error" message={error?.message} />}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="grid" style={{ gridTemplateColumns: 'auto 1fr 1fr', gap: '8px' }}>
          {typeof pulseCheckCategory === 'number' ? (
            <Box display="flex" alignItems="center">
              <PulseCheckCategoryIcon category={pulseCheckCategory} />
            </Box>
          ) : (
            <Box>
              <Controller
                control={control}
                name="icon"
                render={({ field: { value } }) => {
                  return (
                    <Menu>
                      <MenuTrigger>
                        <IconButton
                          variant="subtleInactive"
                          icon={TaskIcons[value || ICON_DEFAULT_VALUE]}
                          showCaret
                          aria-label="Edit icon"
                        />
                      </MenuTrigger>

                      <MenuContent align="start">
                        {Object.values(TaskIcons)
                          .filter(item => Number.isNaN(Number(item)))
                          .map((taskIcon, index) => (
                            <MenuItem
                              key={taskIcon}
                              onSelect={() => {
                                setValue('icon', TaskIcons[taskIcon]);
                              }}
                            >
                              <ListItem icon={TaskIcons[index]}>{TaskNames[index]}</ListItem>
                            </MenuItem>
                          ))}
                      </MenuContent>
                    </Menu>
                  );
                }}
              />
            </Box>
          )}

          <Box style={{ gridColumn: '2 / -1' }}>
            <Input
              {...register('text')}
              placeholder="Type action steps here..."
              formVariant={errors.text ? 'error' : undefined}
              helperText={errors.text?.message}
              aria-label="Action step"
              readOnly={isGenerated}
            />
          </Box>

          {showAssetBucketing && assetBucketOptions.length > 0 && (
            <Box style={{ gridColumn: '2 / -1' }}>
              <Controller
                control={control}
                name="assetBucketIndex"
                render={({ field }) => (
                  <Select
                    innerRef={field.ref}
                    value={assetBucketOptions.find(({ value: v }) => field.value === v)}
                    onChange={opt => field.onChange(opt?.value)}
                    options={assetBucketOptions}
                    placeholder="Select Asset Bucketing"
                    aria-label="Select Asset Bucketing"
                  />
                )}
              />
            </Box>
          )}

          <Box>{/* Empty Cell */}</Box>

          <Box>
            <Controller
              control={control}
              name="assignTo"
              render={({ field }) => (
                <Select
                  value={field.value}
                  placeholder="Assigned to"
                  disabled={isLoadingAssignList}
                  isLoading={isLoadingAssignList}
                  options={data || []}
                  onChange={assignee => setValue('assignTo', assignee)}
                  aria-label="Assigned to"
                />
              )}
            />
          </Box>

          <Box>
            <Controller
              control={control}
              name="dueDate"
              render={({ field }) => (
                <InputMask
                  {...field}
                  value={field.value}
                  mask="99/99/9999"
                  maskPlaceholder={null}
                  placeholder="MM/DD/YYYY"
                  formVariant={errors.dueDate ? 'error' : undefined}
                  helperText={errors.dueDate?.message}
                  aria-label="Due date"
                />
              )}
            />
          </Box>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="flex-end" mt="16">
          <Button
            size="small"
            variant="subtleInactive"
            onClick={() => setFormVisibility(false)}
            disabled={isLoadingUpdateTask || isFetching}
            mr="8"
          >
            Cancel
          </Button>

          <Button
            size="small"
            type="submit"
            disabled={isLoadingUpdateTask || isFetching}
            isLoading={isLoadingUpdateTask || isFetching}
          >
            Save
          </Button>
        </Box>

        <Box height={1} bg="neutral.100" mt="16" mb="24" />
      </form>
    </>
  );
};
